import { useState, type FormEvent } from 'react'

import { useImportPlaylist, useImportSources, type ImportPlaylistResult } from '@/api/hooks'
import { Button, Dialog, Input } from '@/components/ui'

import { describeError } from './helpers'

interface ImportPlaylistDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onImported: (result: ImportPlaylistResult) => void
}

/**
 * Paste a link, get a playlist.
 *
 * Imported songs join the library and download automatically.
 */
export function ImportPlaylistDialog({
  open,
  onOpenChange,
  onImported,
}: ImportPlaylistDialogProps) {
  const importPlaylist = useImportPlaylist()
  const sources = useImportSources()
  const [url, setUrl] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const names = (sources.data?.providers ?? []).map((provider) => provider.name)
  const canSubmit = url.trim().length > 0 && !importPlaylist.isPending

  function close(next: boolean) {
    if (importPlaylist.isPending) return
    if (!next) {
      setUrl('')
      setName('')
      setError(null)
    }
    onOpenChange(next)
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canSubmit) return
    setError(null)
    importPlaylist.mutate(
      { url: url.trim(), name: name.trim() || undefined },
      {
        onSuccess: (result) => {
          close(false)
          onImported(result)
        },
        onError: (mutationError) =>
          setError(describeError(mutationError, 'Could not import that playlist.')),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title="Import a playlist"
      description={
        names.length > 0
          ? `Paste a public playlist link from ${names.join(' or ')}.`
          : 'Paste a public playlist link.'
      }
      size="sm"
      footer={
        <>
          <Button variant="ghost" disabled={importPlaylist.isPending} onClick={() => close(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="import-playlist-form"
            loading={importPlaylist.isPending}
            disabled={!canSubmit}
          >
            Import
          </Button>
        </>
      }
    >
      <form id="import-playlist-form" onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}

        <Input
          label="Playlist link"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://www.deezer.com/playlist/…"
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          inputMode="url"
          required
          disabled={importPlaylist.isPending}
        />

        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Keep the original name"
          autoComplete="off"
          disabled={importPlaylist.isPending}
          hint="Adds every song to your library and downloads it permanently."
        />
      </form>
    </Dialog>
  )
}
